import {
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormArray, FormBuilder } from '@angular/forms';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SearchBarService } from 'src/app/shared/components/search-bar/search-bar.service';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { TableColumnTypeEnum } from 'src/app/shared/components/table/shared/enums';
import { TableConfig, TableFilters } from 'src/app/shared/components/table/shared/models';
import { BasicService } from 'src/app/shared/services/basic.service';

import { AlcmeonAiService } from '../alcmeon-ai.service';
import { AiAgentModeEnum, AiSistantDisplayModeEnum } from '../shared/enums';
import { AiAgent, AiSistant } from '../shared/models';

@Component({
  selector: 'app-ai-ssistant-setup',
  templateUrl: './ai-ssistant-setup.component.html',
  styleUrls: ['./ai-ssistant-setup.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AiSsistantSetupComponent implements OnInit, OnDestroy {
  config: TableConfig = {
    displayedColumns: ['team', 'aiAgent', 'displayMode', 'aiSummary'],
    columns: [
      {
        name: 'team',
        translationKey: 'parameters/alcmeon-ai/setup/table/col/team',
        property: 'teamName',
        type: TableColumnTypeEnum.STRONG,
      },
      {
        name: 'aiAgent',
        translationKey: 'parameters/alcmeon-ai/setup/table/col/ai-agent',
        property: 'aiAgentTpl',
        type: TableColumnTypeEnum.TEMPLATE_REF,
      },
      {
        name: 'displayMode',
        translationKey: 'parameters/alcmeon-ai/setup/table/col/display-mode',
        property: 'displayModeTpl',
        type: TableColumnTypeEnum.TEMPLATE_REF,
      },
      {
        name: 'aiSummary',
        translationKey: 'parameters/alcmeon-ai/setup/table/ai-summary',
        property: 'aiSummaryTpl',
        type: TableColumnTypeEnum.TEMPLATE_REF,
      },
    ],
    actions: [],
  };

  filters: TableFilters = {};

  form = this.formBuilder.group({
    aiAgentFormArray: this.formBuilder.array([]),
  });

  get aiAgentFormArray() {
    return this.form.get('aiAgentFormArray') as FormArray;
  }

  aiAgents: AiAgent[];
  aiAgentsChoices: Choice[];
  displayModesChoices: Choice[];
  aiSummaryChoices: Choice[] = [
    {
      id: true,
      label: this.basicService.translate('parameters/alcmeon-ai/setup/table/ai-summary/enabled'),
    },
    {
      id: false,
      label: this.basicService.translate('parameters/alcmeon-ai/setup/table/ai-summary/disabled'),
    },
  ];

  @ViewChild('aiAgentSelect') aiAgentSelectTplRef: TemplateRef<any>;
  @ViewChild('displayModesSelect') displayModesSelectTplRef: TemplateRef<any>;
  @ViewChild('aiSummarySelect') aiSummarySelectTplRef: TemplateRef<any>;

  @HostListener('window:resize')
  onWindowResizeUpdateActionsPosition() {
    this.searchBarService.checkScrollPosition();
  }

  private subscriptions = new Subscription();

  constructor(
    private alcmeonAiService: AlcmeonAiService,
    private basicService: BasicService,
    private formBuilder: FormBuilder,
    private searchBarService: SearchBarService,
    @Inject('TopToolbarFactory') private topToolbarService: any
  ) {}

  ngOnInit() {
    this.topToolbarService.setTitle(
      `${this.basicService.translate('parameters/alcmeon-ai/title')} › ${this.basicService.translate(
        'parameters/alcmeon-ai/tab/setup'
      )}`,
      null
    );
    this.displayModesChoices = [
      {
        id: AiSistantDisplayModeEnum.init,
        label: this.basicService.translate('parameters/alcmeon-ai/setup/table/display-mode/on-init'),
      },
      {
        id: AiSistantDisplayModeEnum.click,
        label: this.basicService.translate('parameters/alcmeon-ai/setup/table/display-mode/on-click'),
      },
    ];

    this.basicService.showSpinner();
    this.alcmeonAiService
      .getAiAgents({ sort: { active: 'name', direction: 'asc' } })
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe(aiAgents => {
        this.aiAgents = aiAgents.body.filter(aiAgent => aiAgent.isActive && aiAgent.mode === AiAgentModeEnum.aissitant);
        this.aiAgentsChoices = this.aiAgents.map(aiAgent => ({ id: aiAgent.id, label: aiAgent.name }));
        // Option No agent (empty entry)
        this.aiAgentsChoices.unshift({
          id: null,
          label: ``,
        });

        this.searchBarService.refererAsync$ = this.alcmeonAiService.getAissistantSetups;
        this.searchBarService.formatter = this.formatItem;
        this.searchBarService.submitQuery();
      });
  }

  formatItem = (aiSistant: AiSistant) => {
    // value par default
    if (aiSistant.displayMode === null) {
      aiSistant.displayMode = AiSistantDisplayModeEnum.click;
    }
    const aiSistantGroup = this.formBuilder.group({
      aiAgentId: [aiSistant.aiAgentId],
      aissistantConfigId: [aiSistant.aissistantConfigId],
      aiSummary: [aiSistant.aiSummary],
      displayMode: [aiSistant.displayMode],
      routingTagId: [aiSistant.routingTagId],
    });

    this.aiAgentFormArray.push(aiSistantGroup);
    const index = this.aiAgentFormArray.length - 1;

    aiSistant.teamName = this.alcmeonAiService.routingTagsMap.get(aiSistant.routingTagId);

    aiSistant.aiAgentTpl = {
      templateRef: this.aiAgentSelectTplRef,
      context: { control: this.aiAgentFormArray.at(index).get('aiAgentId') },
    };

    aiSistant.displayModeTpl = {
      templateRef: this.displayModesSelectTplRef,
      context: { control: this.aiAgentFormArray.at(index).get('displayMode') },
    };

    aiSistant.aiSummaryTpl = {
      templateRef: this.aiSummarySelectTplRef,
      context: { control: this.aiAgentFormArray.at(index).get('aiSummary') },
    };
  };

  /**
   * Save all items ( also not modified )
   */
  save() {
    this.basicService.showSpinner();
    this.alcmeonAiService
      .saveAissistantSetups(this.aiAgentFormArray.value)
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe(() => {
        this.basicService.toastSuccess('parameters/alcmeon-ai/setup/save-success');
        this.form.markAsPristine();
      });
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
    this.searchBarService.onDidLeave();
  }
}
